<template>
  <div>
    <select-field ref="ContactSummary"
                  :value=display
                  :required=required
                  :editFieldInfo=editFieldInfo
                  :items=items
                  :disabled=readOnly
                  :canEdit=canEdit
                  :selectText=selectText
                  :hideLabel='hideLabel'
                  :dataTable='dataTable'
                  :showMissing=showMissing
                  :notOrdered=notOrdered
                  :fiche=fiche
                  @updated=setUpdated
                  @onEdit=onEdit
                  @error=setError>
    </select-field> 

    <section-container-panel v-if="dialogOpen"
                             :section="section"
                             :dialogOpen="dialogOpen"
                             :extended="true"
                             :helper="helper"
                             :ressourceHeader="ressourceHeader"
                             :fiche="fiche"
                             @updated="setSectionUpdated"
                             @closeDialog="closeDialog"
                             @saveOrCancel="setSaveOrCancel"
                             @status="statusUpdated">
    </section-container-panel>
  </div>
</template>

<script lang="ts">
  import { defineComponent } from "vue";
  import Section from '@/framework/base/classes/Section';
  import { FieldInfo, EditFieldType, FieldStatus } from '@/framework/base/components/FieldInfo'
  import SummaryBase from '@/framework/base/components/SummaryBase';
  import { Sections } from '@/generated/classes/AppSchema';
  import SelectField from '@/framework/base/components/SelectField.vue'
  import Item from '@/framework/base/classes/Item';
  import Contact from '@/extend/classes/Contact';
  import Owner from '@/extend/classes/Owner';
  import SectionContainerPanel from '@/framework/base/components/SectionContainerPanel.vue';
  import SectionContactExtend from '@/extend/sections/SectionContactExtend.vue';
  import { storeContact } from '@/generated/stores';
  import ListData, { ListFilter } from '@/extend/classes/ListData';

  export default defineComponent({
    extends: SummaryBase,
    components: {
      SelectField,
      SectionContainerPanel,
    },

    props: {
      helper: {
        type: Boolean
      }
    },

    beforeUnmount (): void {
      this.clearStore();
    },

    data() {
      const listContact: Contact[] = [];
      const tmpContact: Contact = new Contact();
      return {
        selectedContactId: 0,
        selectedContact: new Contact(),
        tmpContact,
        newContext: true,
        reset: true,
        listUpdated: false,
        listContact
      }
    },

    computed: {
      editFieldInfo (): FieldInfo {
        const type = EditFieldType.SUMMARY;
        const precision = 2;
        const format = null;
        const mask = null;
        const focus = null;
        const live = null;
        return new FieldInfo(this.ressource, type, precision, mask, format, focus, live, this.theme);
      },

      section (): Section {
        const section = new Section();
        section.component = SectionContactExtend;
        section.type = 'contact';
        section.data = this.selectedContact;
        section.picto = 'SectionContactExtendPicto';
        section.contextId = this.contextId;
        section.showButton = true;
        section.autoSave = true;
        if (this.sectionName) {
          section.sectionName = this.sectionName;
        }
        if (this.requiredAttributs) {
          section.requiredAttributs = this.requiredAttributs;
        }
        return section;
      },

      listItems (): Contact[] {
        if (this.reset != this.toggleReset) {
          this.loadItems();
        }
        return this.listContact;
      },

      items (): Item[] {
        const items: Item[] = [];
        items.push(new Item(0, this.newLabel != undefined && this.newLabel != '' ? this.newLabel : "Nouveau contact")); 
        const contacts = this.getItems(this.listItems, this.sectionName);
        contacts.forEach(contact => {
          if (contact && contact.id > 0 ) {
            items.push(new Item(contact.id, this.getDisplay(contact)));
          }
        })
        return items;
      },

      display (): string {
        const value = this.value;
        let item = this.items.find(a => a.key == value)
        this.changeContext();
        if (this.items.length == 2 && this.newContext && this.required && this.autoSelect) {
          this.changeContext();
          item = this.items.find(a => a.key == this.items[1].key);
          if (item && item.key != this.initValue) {
            this.itemChanged(item.key);
            this.setStatusUpdated(true);
            if (this.initValue != 0 || this.items.length != 2) {
              this.statusUpdated(this.status);
            }
          }
        }
        if (item) {
          if (this.resetToggle()) {
            this.setStatusUpdated(true);
            this.statusUpdated(this.status);
          }
          const itemDefined = item;
          const contact = this.listContact.find(a => a.id == itemDefined.key);
          if (contact && contact.id != this.selectedContactId) {
            this.setSelectedContact(contact);
          }
          if (item.key == 0 && this.selectedContactId == 0) {
            this.setSelectedContact(this.newContact());
          }
          this.setSelectedContactId(contact ? contact.id : this.selectedContact.id);
        }
        const res = item && item.key != 0 ? item.title : '';
        if (this.cancelChanges && item) {
          this.itemChanged(item.key);
        }
        this.setAttribut(this.selectedContact, this.owner, this.parentObj)
        return res
      },

      canEdit (): string {
        return true
      }
    },

    methods: {
      setSelectedContact(value: Contact) {
        this.selectedContact = value;
      },
      setSelectedContactId(id: number) {
        this.selectedContactId = id;
      },
      setNewContext(value: boolean) {
        this.newContext = value;
      },
      setStatusUpdated(value: boolean) {
        this.status.updated = value;
      },
      newContact (): Contact {
        this.clearStore();
        const newContact = storeContact.createNewContact();
        this.tmpContact = newContact;
        return newContact;
      },

      loadItems (): Promise<boolean> {
        this.reset = this.toggleReset;
        return new Promise((resolve) => {
          if (this.filter) {
            Contact.list(this.filter)
              .then((data: ListData) => {
                const items = data.reccords as Contact[];
                this.listContact = [];
                items.forEach((item) => {
                  this.listContact.push(Contact.fromJson(item));
                })
                this.listUpdated = true;
                resolve(true)
              }).catch(() => {
                //Catch empty list
              });
          } else {
            resolve(false);
          }
        })
      },

      clearStore() : void {
        if (this.tmpContact != null && this.tmpContact.id < 0) {
          storeContact.mContactDeleted(this.tmpContact.id);
        }
      },

      contextChanged() : void {
        this.newContext = true;
        this.selectedContactId = 0;
        this.selectedContact = new Contact();
        this.initValue = this.value;
      },

      setSaveOrCancel () {
        this.cancelChanges = true;
      },

      // eslint-disable-next-line
      getItems(contact: Contact[], section: String): Contact[] {
        const items: Contact[] = [];
        contact.forEach((item) => {
          items.push(Contact.fromJson(item));
        })
        return items;
      },

        // eslint-disable-next-line
      getDisplay(contact: Contact): string {
        return '';
      },

      // eslint-disable-next-line
      setAttribut(contact: Contact, owner: Owner, parentObj: unknown): void {
        // to implement
      },

      setSectionUpdated(contact: Contact) {
        const item = this.items.find(a => a.key == contact.id);
        if (!item && contact.id > 0) {
          this.loadItems().then(() => {
            const item = this.items.find(a => a.key == contact.id);
            if (item) {
              this.setUpdated(item);
            }
          });
        }
        if (item) {
          this.setUpdated(item);
        }
      },

      setUpdated (item: Item): void {
        if (item.key == 0) {
          this.dialogOpen = true;
          this.selectedContact = this.newContact();
        }
        this.itemChanged(item.key);
        this.status.updated = this.initValue != item.key;
        this.status.error = false;
        this.statusUpdated(this.status);
      },

      setRestored (contact: Contact) {
        this.selectedContact = contact;
      },

      itemChanged (id: number): void {
        this.$emit('update:value', id);
      },

      statusUpdated (status: FieldStatus): void {
        this.$emit('status', status);
      },

      missing (): boolean {
        // @ts-expect-error : generated method
        return this.$refs.ContactSummary.missing();
      },

      touch (): boolean {
        // @ts-expect-error : generated method
        return this.$refs.ContactSummary.touch();
      }
    },
  })
</script>
